/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { queryParam } from '@/util/queryParams'

/**
 * Reads a dev URL flag and stickies it in sessionStorage for the tab session.
 *
 * Reads `?<param>=<value>` first and stickies it under `storageKey`; on a later
 * call it falls back to the stickied value. The SPA router drops the query
 * string once it navigates (e.g. `/chat?agent=bedrock` → `/chat/<sessionId>`),
 * so a URL-only read would resolve at boot but return null by the time the flag
 * is next consulted. Stickying keeps the choice stable for the whole tab
 * session without persisting it across tabs.
 *
 * Raw sessionStorage is deliberate: reads happen synchronously on hot paths
 * (an async persistent item would race them) and the value is an ephemeral dev
 * flag, not app state. Each storage access is guarded so a private-mode Safari
 * throw (or a hostile URL) no-ops gracefully rather than breaking the caller.
 */
export function stickyUrlFlag(param: string, storageKey: string): string | null {
    let fromUrl: string | null = null
    try {
        fromUrl = queryParam(param)?.trim() || null
    } catch {
        fromUrl = null
    }
    if (fromUrl) {
        try {
            sessionStorage.setItem(storageKey, fromUrl)
        } catch {
            /* private-mode Safari — the in-URL value still works this call. */
        }
        return fromUrl
    }
    try {
        return sessionStorage.getItem(storageKey)?.trim() || null
    } catch {
        return null
    }
}
